import Vue from "vue";

// Single shared <audio> element that lives outside any view, so playback
// survives route changes.
const state = Vue.observable({
  nowPlaying: { id: null, title: "", artist: "", color: "", cover: "", paused: true },
  queue: [],
  queueIndex: -1,
  currentTime: 0,
  duration: 0,
});

let audio = null;
let playCb = null;

// ── Verified-listening accumulator ───────────────────────────────
// Counts only seconds that actually played (small forward steps of the audio
// clock — seeking or scrubbing adds nothing) and hands them to a listener in
// batches. The server re-validates everything; this just keeps traffic low.
let listenCb = null;
let listenAccum = 0;
let listenLastT = 0;
let listenEntry = null;

function flushListen() {
  if (!listenEntry || listenAccum < 1) return;
  const seconds = Math.round(listenAccum);
  listenAccum -= seconds;
  if (listenCb) { try { listenCb({ trackId: listenEntry.id, artistId: listenEntry.artistId, seconds }); } catch (e) { console.error(e); } }
}
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (document.hidden) flushListen(); });
}

function ensureAudio() {
  if (audio) return audio;
  audio = new Audio();
  audio.preload = "auto";
  audio.addEventListener("play", () => { if (state.nowPlaying.id) state.nowPlaying.paused = false; });
  audio.addEventListener("pause", () => { if (state.nowPlaying.id) state.nowPlaying.paused = true; flushListen(); });
  audio.addEventListener("timeupdate", () => {
    const t = audio.currentTime || 0;
    state.currentTime = t;
    const d = t - listenLastT;
    if (!audio.paused && listenEntry && d > 0 && d <= 1.5) {
      listenAccum += d;
      if (listenAccum >= 60) flushListen();
    }
    listenLastT = t;
  });
  audio.addEventListener("loadedmetadata", () => { state.duration = audio.duration || 0; });
  audio.addEventListener("ended", () => {
    if (state.queueIndex >= 0 && state.queueIndex < state.queue.length - 1) playNext();
    else stop();
  });
  return audio;
}

function normalize(e) {
  return {
    id: e.id,
    title: e.title || e.track || "Untitled",
    artist: e.artist || e.artistName || "",
    color: e.color || e.coverColor || "#7c5cff",
    cover: e.cover || e.coverImageUrl || "",
    audioUrl: e.audioUrl,
    artistId: e.artistId || "",
  };
}

function start(entry) {
  const a = ensureAudio();
  flushListen();
  listenEntry = entry; listenAccum = 0; listenLastT = 0;
  state.nowPlaying = { id: entry.id, title: entry.title, artist: entry.artist, color: entry.color, cover: entry.cover || "", paused: false };
  state.currentTime = 0;
  state.duration = 0;
  a.src = entry.audioUrl;
  a.play().catch((err) => console.warn("Playback failed", err));
  if (playCb) { try { playCb(entry); } catch (e) { console.error(e); } }
}

function play(entry, queueList) {
  if (!entry || !entry.audioUrl) return;
  const e = normalize(entry);
  if (state.nowPlaying.id === e.id) { toggle(); return; }
  if (Array.isArray(queueList) && queueList.length) {
    state.queue = queueList.filter((q) => q && q.audioUrl).map(normalize);
    state.queueIndex = state.queue.findIndex((q) => q.id === e.id);
  } else {
    state.queue = [e];
    state.queueIndex = 0;
  }
  start(e);
}

function toggle() {
  const a = ensureAudio();
  if (!state.nowPlaying.id) return;
  if (a.paused) a.play().catch(() => {}); else a.pause();
}

function playNext() {
  if (state.queueIndex < state.queue.length - 1) {
    state.queueIndex += 1;
    start(state.queue[state.queueIndex]);
  }
}

function playPrevious() {
  const a = ensureAudio();
  if (a.currentTime > 3 || state.queueIndex <= 0) { a.currentTime = 0; return; }
  state.queueIndex -= 1;
  start(state.queue[state.queueIndex]);
}

function seekToPct(p) {
  const a = ensureAudio();
  if (a.duration) a.currentTime = Math.max(0, Math.min(1, p)) * a.duration;
}

function stop() {
  flushListen();
  listenEntry = null; listenAccum = 0; listenLastT = 0;
  if (audio) { audio.pause(); audio.removeAttribute("src"); audio.load(); }
  state.nowPlaying = { id: null, title: "", artist: "", color: "", paused: true };
  state.queue = [];
  state.queueIndex = -1;
  state.currentTime = 0;
  state.duration = 0;
}

function onPlay(cb) { playCb = cb; }
function onListen(cb) { listenCb = cb; }

export default { state, play, toggle, playNext, playPrevious, seekToPct, stop, onPlay, onListen };